/**
 * The render layer: the only file here that touches the DOM.
 *
 * Everything it draws comes from `ui/model.ts` and everything it offers comes from `ui/affordances.ts`.
 * That split is not tidiness - it is what makes the rules testable. A button invented here would have
 * no entry in the catalog and no canonical input behind it, which is the one failure workspace-client.md
 * §3 cannot tolerate.
 *
 * Accessibility is a build-step-1 requirement, not a polish pass (§3): a messenger of nested divs,
 * threads and unread badges is far easier to get wrong than an 80-column log and is aimed at a wider
 * audience. So: real `<button>`s (keyboard-operable for free), the message list is a polite `role="log"`
 * that announces rather than moving focus (§2a's never-auto-switch, restated as an assistive-technology
 * contract), and the only focus this client ever takes is the FIRST one - the buddy DM's composer, once,
 * on the first render (§1a).
 */
import { MAX_REASON, affordancesFor, verbLabel, withReason } from '../affordances.ts';
import type { Affordance } from '../affordances.ts';
import { monogram, systemBot } from '../cast.ts';
import type { CastMember } from '../cast.ts';
import { t } from '../i18n.ts';
import { dayLabel } from '../time.ts';
import type { ChannelView, Finale, MessageAction, MessageView, WorkspaceModel } from '../model.ts';
import type { SessionListItem } from '@core';
import {
  DM_BUDDY,
  applyMention,
  deckOffer,
  mentionCandidates,
  mentionQuery,
  mentionSeat,
} from '../model.ts';
import { readsAsCommand } from '../buddy.ts';
import type { WorkspaceApi } from '../../app/workspaceApp.ts';

const MOODS: {
  key: 'gesture.moodGood' | 'gesture.moodFlat' | 'gesture.moodRough';
  mood: string;
}[] = [
  { key: 'gesture.moodGood', mood: 'good' },
  { key: 'gesture.moodFlat', mood: 'flat' },
  { key: 'gesture.moodRough', mood: 'rough' },
];

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, className: string, onClick: () => void): HTMLButtonElement {
  const b = el('button', className, label);
  b.type = 'button';
  b.addEventListener('click', onClick);
  return b;
}

function avatar(who: CastMember, size: 'sm' | 'md' = 'md'): HTMLElement {
  const wrap = el('span', `ws-avatar ws-avatar-${size} ws-avatar-${who.kind}`);
  if (who.avatar) {
    const img = el('img');
    img.src = who.avatar;
    img.alt = '';
    img.width = size === 'sm' ? 24 : 36;
    img.height = size === 'sm' ? 24 : 36;
    img.loading = 'lazy';
    wrap.append(img);
  } else {
    wrap.textContent = monogram(who.name);
  }
  return wrap;
}

/**
 * The all-hands as plain text (§2k: one share affordance, exporting it clean).
 *
 * Self-contained and id-free by construction - it is built from the same slides the screen shows, and
 * those carry display names only. Plain text because that is what survives being pasted into the place
 * a team actually talks.
 */
function shareText(slides: { title: string; lines: string[] }[]): string {
  return slides
    .filter((s) => s.title !== t('finale.letter')) // the letter is yours; it does not go in the export
    .map((s) => [s.title, ...s.lines].join('\n'))
    .join('\n\n');
}

export class WorkspaceUi {
  /** Which channel is open. Never changed by an incoming message (§2a: no channel steals focus). */
  private current = DM_BUDDY;
  private firstRenderDone = false;
  /** The sign-off two-tap, open or not (§2m). */
  private signingOff = false;
  /**
   * Which all-hands slide is showing, or null when the frame is intact (§2f).
   *
   * Opened automatically the first time an ending arrives and closeable afterwards, because §2f asks
   * for both: the frame BREAKS (you do not go looking for the ending), and then the workspace stays
   * readable - the office after everyone went home - with the finale re-openable from a pin.
   */
  private slide = 0;

  /**
   * Whether the player has PUT THE ENDING AWAY. Derived state, not a caught event - and that
   * distinction is the bug this replaced.
   *
   * The first version latched `sawEnding` inside `render()` the first time a finale appeared. That is
   * an event catch dressed as state: if the ending landed mid-render the latch was set by a pass that
   * never became the overlay, and the result was a state with no way back - session over, pin present,
   * takeover never opened and never dismissed, so nothing could reopen it. It survived on a fast
   * machine because the event and the render happened to coincide there.
   *
   * Now the overlay is a function of the model: an ending exists and you have not dismissed it. There
   * is no moment to miss.
   */
  private dismissed = false;
  private copied = false;

  /** An open promise form (§7.5b), or null. */
  private promising: { action: MessageAction } | null = null;
  private promiseWhat = '';
  private promiseBy = '';

  /** A confirm the ceremony ladder asked for (§2b), or null. */
  private confirming: { action: MessageAction } | null = null;
  private confirmNote = '';
  /** What is in the composer, per channel — switching rooms must not eat a half-typed line. */
  private readonly drafts = new Map<string, string>();
  private signInName = '';
  /** The `@`-token being typed, or null when the composer is not in a mention. */
  private mentioning: string | null = null;
  private newName = '';

  constructor(
    private readonly mount: HTMLElement,
    private readonly api: WorkspaceApi,
  ) {}

  render(): void {
    const scroll = this.mount.querySelector('.ws-messages')?.scrollTop ?? null;

    // The whole tree is rebuilt on every store change, which would otherwise drop focus and the caret
    // the moment anybody else in the room said anything - a messenger that eats your cursor while you
    // are typing is worse than one that is merely ugly. Remembered by id and put back, so what a
    // message ARRIVING does to focus is nothing (§2a's never-auto-switch, as an actual behaviour).
    const focused = document.activeElement;
    const focusedId = focused instanceof HTMLElement ? focused.id : '';
    const caret = focused instanceof HTMLInputElement ? focused.selectionStart : null;

    this.mount.replaceChildren(this.api.isAuthenticated() ? this.workspace() : this.entry());

    // Keep the reader where they were. A messenger that jumps to the bottom while you are reading
    // history is the thing everyone hates about messengers.
    const list = this.mount.querySelector('.ws-messages');
    if (list instanceof HTMLElement) {
      list.scrollTop = scroll ?? list.scrollHeight;
    }

    const composer = this.mount.querySelector('#ws-composer');
    if (!this.firstRenderDone && this.api.inWorkspace() && composer instanceof HTMLElement) {
      // §1a: the buddy DM is rendered first and FOCUSED, before any channel - the mental model is set
      // before the composer is touched. This is the only focus this client ever TAKES, and it happens
      // once, on the first render of a room.
      this.firstRenderDone = true;
      composer.focus();
      return;
    }

    if (focusedId) {
      const again = this.mount.querySelector(`#${CSS.escape(focusedId)}`);
      if (again instanceof HTMLElement) {
        again.focus();
        if (again instanceof HTMLInputElement && caret !== null) {
          again.setSelectionRange(caret, caret);
        }
      }
    }
  }

  // ---- the door ------------------------------------------------------------------------------------

  private entry(): HTMLElement {
    const page = el('div', 'ws-entry');
    const card = el('section', 'ws-entry-card');
    card.append(el('h1', 'ws-entry-title', t('app.title')));

    const label = el('label', 'ws-field');
    label.append(el('span', 'ws-field-label', t('app.signInName')));
    const input = el('input', 'ws-input');
    input.id = 'ws-signin';
    input.value = this.signInName;
    input.addEventListener('input', () => (this.signInName = input.value));
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') void this.api.signIn(this.signInName);
    });
    label.append(input);
    card.append(label);
    card.append(
      button(t('app.signInGo'), 'ws-primary', () => void this.api.signIn(this.signInName)),
    );

    for (const line of this.api.notices()) {
      card.append(el('p', 'ws-notice', line));
    }

    page.append(card);
    return page;
  }

  // ---- the workspace -------------------------------------------------------------------------------

  private workspace(): HTMLElement {
    const model = this.api.model();
    const shell = el('div', 'ws-shell');

    if (!this.api.inWorkspace()) {
      // Signed in but not in a room yet: the door, one step further in.
      shell.append(this.picker());
      return shell;
    }

    shell.append(this.rail(model));
    shell.append(this.room(model));
    shell.append(this.people(model));

    // Reading a room marks it read - but only AFTER the frame that showed you the divider, or the
    // "new since you left" line would erase itself in the same paint that drew it.
    const open = model.channels.find((c) => c.id === this.current);
    if (open) {
      const { id } = open;
      // `?? 0` matters: opening a room that is EMPTY still means you have seen everything in it, and
      // without a mark of its own such a room could never go unread later - which is exactly the room
      // most likely to receive the first message somebody wants you to notice. Every committed message
      // sorts above 0 (seqs start at 1), so 0 is "read, and nothing was here".
      const upTo = open.messages[open.messages.length - 1]?.order ?? 0;
      queueMicrotask(() => this.api.markRead(id, upTo));

      // "Left on read" becomes DIEGETIC here (§2i). Read state has been client-local since step 1 and
      // characters were explicitly forbidden from referencing it, precisely so content could never
      // fake a fact the engine did not hold. It holds it now: this commits the marker for messages
      // from the OUTSIDE WORLD, which are the only ones §2i makes diegetic - a colleague's sense of
      // being ignored lives in NPC memory instead, which the reaction fold already feeds.
      for (const message of open.messages) {
        if (message.seq !== null && message.sender.id.startsWith('stakeholder:')) {
          void this.api.markSeen(message.sender.id.replace('stakeholder:', ''), message.seq);
        }
      }
    }

    if (this.confirming) {
      shell.append(this.confirmDialog(model));
    }
    if (this.promising) {
      shell.append(this.promiseDialog());
    }

    // ACT TWO (§2f). The ending is felt in the channels first - the outcome, the stakeholders, the
    // room going quiet - and THEN the frame breaks. Derived, so there is no arrival to miss: while an
    // ending exists and has not been put away, the takeover IS the screen.
    if (model.finale && !this.dismissed) {
      shell.append(this.allHands(model.finale));
    }

    return shell;
  }

  private picker(): HTMLElement {
    const page = el('div', 'ws-entry');
    const card = el('section', 'ws-entry-card');
    card.append(el('h1', 'ws-entry-title', t('app.entryTitle')));

    // SECTIONED, YOURS FIRST (DEC-18). This matters more here than on the terminal: with sessions
    // private by default the public directory is usually EMPTY, so a door that only browsed it would
    // show a returning player nothing at all - including the team they left ten minutes ago.
    const section = (
      titleKey: 'app.myWorkspaces' | 'app.openWorkspaces',
      items: SessionListItem[],
    ) => {
      if (items.length === 0) {
        return;
      }

      card.append(el('h2', 'ws-list-heading', t(titleKey)));
      const list = el('ul', 'ws-list');
      for (const item of items) {
        const row = el('li', 'ws-list-row');
        row.append(el('span', 'ws-list-name', item.name));
        row.append(
          button(t('app.enter'), 'ws-secondary', () => void this.api.openWorkspace(item.id)),
        );
        list.append(row);
      }
      card.append(list);
    };

    const mine = this.api.myWorkspaces();
    section('app.myWorkspaces', mine);
    section('app.openWorkspaces', this.api.openWorkspaces());

    if (mine.length === 0 && this.api.openWorkspaces().length === 0) {
      // The honest empty state, and now the COMMON one: a first-time player has no teams and the
      // directory is empty by design. Saying so beats a bare heading over nothing, and the next move
      // is already the field below.
      card.append(el('p', 'ws-list-empty', t('app.noWorkspaces')));
    }

    const label = el('label', 'ws-field');
    label.append(el('span', 'ws-field-label', t('app.newWorkspaceName')));
    const input = el('input', 'ws-input');
    input.id = 'ws-new';
    input.value = this.newName;
    input.addEventListener('input', () => (this.newName = input.value));
    label.append(input);
    card.append(label);

    // The deck choice (DEC-17 M4 increment 8b). Rendered only when the catalog has more than one
    // thing in it: a picker offering one option is a question with no answer, and it would push the
    // button that actually starts a team further down the page for nothing.
    const decks = this.api.scenarios();
    if (decks.length > 1) {
      const deckField = el('label', 'ws-field');
      deckField.append(el('span', 'ws-field-label', t('app.newWorkspaceDeck')));
      const select = el('select', 'ws-input') as HTMLSelectElement;
      select.id = 'ws-deck';
      for (const deck of decks) {
        const option = el('option', '') as HTMLOptionElement;
        option.value = deck.ref;
        // The FRAME phrases the offer, because the two are genuinely different invitations rather
        // than two lengths of the same one.
        option.textContent = deckOffer(deck);
        option.selected = deck.ref === this.api.chosenScenario();
        select.append(option);
      }

      select.addEventListener('change', () => this.api.chooseScenario(select.value));
      deckField.append(select);
      card.append(deckField);
    }
    card.append(
      button(
        t('app.newWorkspace'),
        'ws-primary',
        () => void this.api.createWorkspace(this.newName || 'Acme'),
      ),
    );

    for (const line of this.api.notices()) {
      card.append(el('p', 'ws-notice', line));
    }

    page.append(card);
    return page;
  }

  private rail(model: WorkspaceModel): HTMLElement {
    const rail = el('nav', 'ws-rail');
    rail.setAttribute('aria-label', t('app.channels'));
    rail.append(el('div', 'ws-rail-name', model.name));

    // The PIN (§2f). After the all-hands, the workspace stays readable - the office after everyone
    // went home - and this is how you get the ending back. It exists only once there is one, so it is
    // never a promise of an ending that has not happened.
    if (model.finale) {
      rail.append(
        button(t('finale.open'), 'ws-rail-pin', () => {
          this.dismissed = false;
          this.slide = 0;
          this.copied = false;
          this.render();
        }),
      );
      rail.append(el('p', 'ws-rail-after', t('finale.after')));
    }

    // The milestone (DEC-17 M1), above the calendar because it is the larger clock: the calendar says
    // what is on Thursday, this says what Thursday is FOR. §2's whole time framing was milestone
    // language written before there were milestones to point at.
    if (model.milestone) {
      const strip = el('div', 'ws-milestone');
      strip.append(el('span', 'ws-milestone-label', t('milestone.label')));
      strip.append(el('span', 'ws-milestone-now', model.milestone.current));
      if (model.milestone.next) {
        strip.append(
          el(
            'span',
            'ws-milestone-next',
            model.milestone.unlocks.length > 0
              ? t('milestone.nextOpens', {
                  next: model.milestone.next,
                  unlocks: model.milestone.unlocks.join(', '),
                })
              : t('milestone.next', { next: model.milestone.next }),
          ),
        );

        // WHAT IT TAKES. Naming the gate without naming its condition is a signpost with no distance
        // on it: it tells you where you are going and nothing about how to get there.
        if (model.milestone.nextNeeds) {
          strip.append(
            el(
              'span',
              'ws-milestone-needs',
              t('milestone.needs', { needs: model.milestone.nextNeeds }),
            ),
          );
        }
      }
      rail.append(strip);
    }

    // The calendar strip (§2a): meetings appear on calendars before they happen, which is the whole
    // reason a scheduled standup stops being an ambush. Absent when the engine knows of nothing.
    if (model.calendar) {
      const strip = el('div', 'ws-calendar');
      if (model.calendar.imminent) strip.classList.add('is-imminent');
      strip.append(el('span', 'ws-calendar-label', t('calendar.label')));
      strip.append(el('span', 'ws-calendar-text', model.calendar.text));
      rail.append(strip);
    }

    const groups: [string, ChannelView['kind'][]][] = [
      [t('app.channels'), ['dm', 'channel']],
      [t('app.pages'), ['page']],
    ];

    for (const [heading, kinds] of groups) {
      const channels = model.channels.filter((c) => kinds.includes(c.kind));
      if (channels.length === 0) continue;

      rail.append(el('h2', 'ws-rail-heading', heading));
      const list = el('ul', 'ws-rail-list');
      for (const channel of channels) {
        const item = el('li');
        const b = button(channel.label, 'ws-rail-item', () => {
          this.current = channel.id;
          this.render();
        });
        if (channel.id === this.current) {
          b.classList.add('is-current');
          b.setAttribute('aria-current', 'true');
        }
        if (channel.tone === 'alarm') b.classList.add('is-alarm');

        // The corridor (§2a): a count of what landed while you were elsewhere, and the first line of
        // it. Neither ever switches the channel - awareness, not a summons.
        if (channel.unread > 0) {
          b.classList.add('is-unread');
          const badge = el('span', 'ws-badge', String(channel.unread));
          badge.setAttribute('aria-label', t('corridor.unread', { n: channel.unread }));
          b.append(badge);
        }
        item.append(b);
        if (channel.murmur) {
          item.append(el('span', 'ws-murmur', channel.murmur));
        }
        list.append(item);
      }
      rail.append(list);
    }

    rail.append(button(t('app.leave'), 'ws-quiet', () => void this.api.leave()));
    return rail;
  }

  private room(model: WorkspaceModel): HTMLElement {
    const channel = model.channels.find((c) => c.id === this.current) ?? model.channels[0]!;
    const main = el('main', 'ws-room');

    const header = el('header', 'ws-room-header');
    header.append(el('h1', 'ws-room-title', channel.label));
    if (channel.topic) {
      const topic = el('p', 'ws-room-topic', channel.topic);
      if (channel.tone === 'alarm') topic.classList.add('is-alarm');
      header.append(topic);
    }
    main.append(header);

    if (channel.kind === 'page') {
      main.append(this.readings(channel));
      return main;
    }

    main.append(this.messages(model, channel));

    if (model.lobby) {
      main.append(this.lobbyBar(model));
    }

    // "X is typing…" at the foot of the room, where every messenger puts it. Only ever what the
    // server says is happening: this client never invents a typist to soften a pause (§2j).
    const typing = model.typing.filter((r) => r.channel === channel.id);
    if (typing.length) {
      const row = el('p', 'ws-typing');
      row.setAttribute('aria-live', 'polite');
      row.textContent = t('typing.one', { name: typing.map((r) => r.who.name).join(', ') });
      main.append(row);
    } else if (
      channel.kind === 'dm' &&
      this.api.askingSeats().includes(channel.id.replace('dm:seat:', ''))
    ) {
      // A question of YOURS in flight. The server's own typing frame (§2d′) covers what the room can
      // see; this covers the one thread it cannot, because a private question that sits in silence
      // reads as being ignored rather than as being thought about.
      const row = el('p', 'ws-typing');
      row.setAttribute('aria-live', 'polite');
      row.textContent = t('dm.thinking');
      main.append(row);
    }

    const standing = this.roleStanding(model);
    if (standing) main.append(standing);

    main.append(this.composer(model, channel));
    for (const line of this.api.notices()) {
      main.append(el('p', 'ws-notice', line));
    }
    return main;
  }

  /**
   * The standing line for somebody with no role (DEC-19). Permanent, not a toast: the notice they were
   * sent on the way in scrolls away, and the STATE it described does not.
   *
   * The playtest's second finding was that a player with no role had no path to one and nothing on
   * screen said so - a silent read-only mode is indistinguishable from a broken app. Two sentences,
   * because there are two different situations and only one of them has a move in it.
   */
  private roleStanding(model: WorkspaceModel): HTMLElement | null {
    if (model.you.seat) {
      return null;
    }

    return el(
      'p',
      'ws-notice',
      model.members.some((m) => m.claimable) ? t('room.pickRole') : t('room.watching'),
    );
  }

  /** A page (§2o): a meter wearing its own fiction. Read-only — there is nothing to type at a graph. */
  private readings(channel: ChannelView): HTMLElement {
    const wrap = el('section', 'ws-page');
    if (channel.readings.length === 0) {
      wrap.append(el('p', 'ws-empty', channel.emptyText));
      return wrap;
    }

    for (const reading of channel.readings) {
      const card = el('div', `ws-reading is-${reading.tone}`);
      card.append(el('span', 'ws-reading-label', reading.label));
      card.append(el('strong', 'ws-reading-value', reading.value));
      if (reading.note) card.append(el('span', 'ws-reading-note', reading.note));
      wrap.append(card);
    }
    return wrap;
  }

  private messages(model: WorkspaceModel, channel: ChannelView): HTMLElement {
    const list = el('div', 'ws-messages');
    // Announces, never steals: the terminal's scrollback contract, restated for a room that updates
    // while you are reading a different one.
    list.setAttribute('role', 'log');
    list.setAttribute('aria-live', 'polite');
    list.setAttribute('aria-label', channel.label);

    if (channel.messages.length === 0) {
      list.append(el('p', 'ws-empty', channel.emptyText));
      return list;
    }

    // §2r: "new since you left" is the watermark's visible half, and for async play it is the primary
    // navigation gesture rather than polish. Drawn once, before the first message this reader has not
    // seen - and only when there is older history to separate it from.
    const firstUnread =
      channel.unread > 0 && channel.unread < channel.messages.length
        ? channel.messages[channel.messages.length - channel.unread]?.id
        : null;

    let lastDay = '';
    for (const message of channel.messages) {
      const label = dayLabel(message.day);
      if (label !== lastDay) {
        lastDay = label;
        const isToday = label === dayLabel(model.day);
        list.append(
          el(
            'div',
            'ws-day',
            isToday ? t('day.today', { day: label }) : t('day.divider', { day: label }),
          ),
        );
      }
      if (message.id === firstUnread) {
        list.append(el('div', 'ws-new-since', t('corridor.newSince')));
      }
      list.append(this.message(model, message));
    }

    return list;
  }

  /**
   * The reactions on a message, and the way to add one (§2i).
   *
   * Cheap by design and quiet by design: no confirm, no notice on failure, no meter. Players trained
   * by the medium expect this to cost nothing, and a gesture that opened a dialog would be a different
   * gesture. The set is closed and mirrors the server's, so an unknown emoji cannot be sent at all.
   */
  private reactions(message: MessageView): HTMLElement {
    const row = el('div', 'ws-msg-reactions');

    for (const reaction of message.reactions) {
      row.append(el('span', 'ws-reaction ws-reaction-on', reaction));
    }

    // Yours, before the server has confirmed it (the outbox). Marked rather than styled identically:
    // the tap has to land instantly, and it also has to be honest about not having arrived yet.
    for (const waiting of message.pendingReactions ?? []) {
      const chip = el('span', `ws-reaction ws-reaction-${waiting.state}`, waiting.reaction);
      chip.title = t(waiting.state === 'failed' ? 'outbox.failed' : 'outbox.sending');
      row.append(chip);
    }

    const add = el('details', 'ws-react');
    const summary = el('summary', 'ws-react-open', t('react.add'));
    summary.title = t('react.addHint');
    add.append(summary);

    const choices = el('div', 'ws-react-choices');
    for (const choice of this.api.reactionChoices()) {
      choices.append(
        button(choice, 'ws-reaction', () => {
          void this.api.react(message.seq!, choice);
          add.removeAttribute('open');
        }),
      );
    }
    add.append(choices);
    row.append(add);
    return row;
  }

  private message(model: WorkspaceModel, message: MessageView): HTMLElement {
    const row = el('article', `ws-msg ws-msg-${message.kind}`);
    if (message.pending) {
      // Stage one, visibly. It reads as a message because it IS one - it just has not landed yet.
      row.classList.add('ws-msg-pending', `ws-msg-${message.pending.state}`);
    }

    row.dataset.messageId = message.id;
    row.append(avatar(message.sender));

    const body = el('div', 'ws-msg-body');
    const head = el('div', 'ws-msg-head');
    head.append(el('span', 'ws-msg-sender', message.sender.name));
    if (message.clock) {
      // Game time on the line, real time on hover — the two clocks never share a stage (§2m).
      const time = el('time', 'ws-msg-time', message.clock);
      if (message.realTime) time.title = message.realTime;
      head.append(time);
    }
    body.append(head);
    body.append(el('p', 'ws-msg-text', message.text));
    if (message.note) body.append(el('p', 'ws-msg-note', message.note));

    // STAGE TWO, when it went wrong. The words are still here, so the move is one tap and not a
    // reconstruction from memory - which is the whole reason a refused line is kept rather than
    // deleted with a banner over it.
    if (message.pending) {
      const status = el('div', 'ws-msg-outbox');
      status.append(
        el(
          'span',
          'ws-msg-outbox-state',
          t(message.pending.state === 'failed' ? 'outbox.failed' : 'outbox.sending'),
        ),
      );

      if (message.pending.state === 'failed') {
        const cmdId = message.pending.cmdId;
        status.append(
          button(t('outbox.retry'), 'ws-outbox-retry', () => void this.api.retry(cmdId)),
        );
        status.append(
          button(t('outbox.discard'), 'ws-outbox-discard', () => this.api.discard(cmdId)),
        );
      }

      body.append(status);
    }

    // Reactions (§2i). Only on committed messages: a reaction points at a seq, and the buddy's
    // scripted lines and the ephemeral incident card are not things the log holds.
    if (message.seq !== null) {
      body.append(this.reactions(message));
    }

    if (message.actions.length) {
      const actions = el('div', 'ws-msg-actions');
      // The prompt belongs to the MOMENT, not to the button row: "what do you want to do?" is what an
      // incident asks, and a standup asking it would be the war-room's voice coming out of a meeting.
      if (message.kind === 'incident') {
        actions.append(el('p', 'ws-msg-prompt', t('incident.pick')));
      }
      for (const action of message.actions) {
        const b = button(action.label, 'ws-reply', () => this.chooseOption(model, action));
        b.dataset.optionId = action.id;
        if (action.odds) b.title = t('incident.bet', { odds: action.odds });
        actions.append(b);
      }
      body.append(actions);
    }

    row.append(body);
    return row;
  }

  /**
   * The ceremony ladder (§2b), applied. A texture card is one click, because it is small and treating it
   * as small is honest. A consequence or a scripted beat opens the confirm and asks for one line of why,
   * which the room reads back later.
   */
  private chooseOption(model: WorkspaceModel, action: MessageAction): void {
    if (action.ceremony === 'promise') {
      // A promise needs WORDS and a DAY, and neither is a choice from a list - so it gets its own
      // small form rather than being squeezed into the option confirm. The words matter more than
      // usual here: the stakeholder's memory keeps them and quotes them back (npc-voice §4.4).
      this.promising = { action };
      this.promiseWhat = '';
      // A couple of days out, in the ROUND the engine counts - the same number the board's due
      // dates use, so the two clocks cannot disagree about one commitment.
      this.promiseBy = String(model.round + 2);
      this.render();
      return;
    }
    if (action.ceremony === 'immediate') {
      void this.api.dispatch(action.input);
      return;
    }

    void model;
    this.confirming = { action };
    this.confirmNote = '';
    this.render();
  }

  /**
   * The promise form (npc-llm-layer §7.5b).
   *
   * Two fields, because the engine's promise has two: what you said you would do, and the round by
   * which it counts as done. The day is pre-filled a couple ahead rather than left blank - a form that
   * makes you invent a deadline from nothing is a form people abandon.
   */
  /**
   * THE ALL-HANDS (§2f), and the artifact (§2k).
   *
   * The workspace dims and this takes over, because a session's end rendered as one more thread is
   * anticlimax as design - the ending is the one moment that is NOT another day at work.
   *
   * Its production is the same whatever happened, with the CELEBRATION removed on a loss rather than
   * replaced by a mirror of it: a collapse rendered as a failed win punishes the player twice for one
   * outcome, and §2d's register for the low end is quiet, not confetti with a minus sign.
   *
   * Screenshot-complete (§2k): display names only, no ids anywhere, and everything needed to read it
   * is on the slide - this is the one screen a team will actually share.
   */
  private allHands(finale: Finale): HTMLElement {
    const slides = this.slidesOf(finale);
    const at = Math.min(this.slide, slides.length - 1);

    const overlay = el('div', `ws-allhands ws-allhands-${finale.outcome}`);
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-labelledby', 'ws-allhands-title');

    const stage = el('section', 'ws-slide');

    // The host is the MEETING-NOTES BOT (§2n): the narrator is decomposed, so the all-hands is chaired
    // by the thing a workspace actually has. The characters speak for themselves inside it.
    const host = el('p', 'ws-slide-host', systemBot('meeting').name);
    stage.append(host);

    const title = el('h1', 'ws-slide-title', slides[at]!.title);
    title.id = 'ws-allhands-title';
    stage.append(title);

    for (const line of slides[at]!.lines) {
      stage.append(el('p', 'ws-slide-line', line));
    }

    // The fidelity badge follows the story INTO the all-hands (§2f). A finale is the worst possible
    // place to start pretending, because it is the one screen the team will screenshot.
    if (finale.source && finale.source !== 'llm' && finale.pages.length) {
      stage.append(el('p', 'ws-slide-badge', t('finale.scripted')));
    }

    const nav = el('div', 'ws-slide-nav');
    if (at > 0) {
      nav.append(
        button(t('finale.back'), 'ws-secondary', () => {
          this.slide = at - 1;
          this.render();
        }),
      );
    }
    if (at < slides.length - 1) {
      nav.append(
        button(t('finale.next'), 'ws-primary', () => {
          this.slide = at + 1;
          this.render();
        }),
      );
    }

    // ONE share affordance, exporting it clean (§2k). Plain text: it survives being pasted anywhere,
    // which is what "shareable" means outside a screenshot.
    nav.append(
      button(this.copied ? t('finale.shared') : t('finale.share'), 'ws-secondary', () => {
        void navigator.clipboard?.writeText(shareText(slides));
        this.copied = true;
        this.render();
      }),
    );

    nav.append(
      button(t('finale.close'), 'ws-secondary', () => {
        this.dismissed = true;
        this.render();
      }),
    );

    stage.append(nav);
    overlay.append(stage);
    return overlay;
  }

  /**
   * The slides. Outcome, then the story, then the calls, then your letter.
   *
   * The order is the argument: what happened, how it read, what YOU did, and then - alone, last, on
   * your screen only - what it meant for you.
   */
  private slidesOf(finale: Finale): { title: string; lines: string[] }[] {
    const slides: { title: string; lines: string[] }[] = [
      {
        // The A5 ending moment: the outcome as ONE slide, in the engine's own words. A score on a
        // slide is a result; the condition that produced it is machinery and stays off (§2g).
        title: finale.headline,
        lines: finale.score === null ? [] : [t('finale.score', { score: String(finale.score) })],
      },
    ];

    // THE SURVIVAL BREAKDOWN, at a collapse (DEC-17 M4 §3). Its own slide, right after the ending, and
    // in the room's own words rather than as a formula: what a team wants at this moment is "we lasted
    // nineteen days and reached four milestones", not `W_final + 0.5*W_peak`.
    if (finale.survival) {
      const sv = finale.survival;
      slides.push({
        title: t('finale.survival'),
        lines: [
          t('finale.survivalLasted', { days: String(sv.rounds) }),
          t('finale.survivalGates', { gates: String(sv.gates) }),
          t('finale.survivalPeak', { peak: String(sv.peak), final: String(sv.final) }),
        ],
      });
    }

    // §2j: short pages, not an essay. The team moves through them together.
    for (const page of finale.pages) {
      slides.push({ title: t('finale.story'), lines: [page] });
    }

    if (finale.decisions.length) {
      slides.push({
        title: t('finale.calls'),
        lines: finale.decisions.flatMap((d) => [
          d.how ? `${d.chose} - ${d.how}` : d.chose,
          ...d.quotes.map((q) => `${q.who}: \u201c${q.text}\u201d`),
        ]),
      });
    }

    if (finale.letter) {
      // Private, on this screen only - which is what the per-player slice always was.
      slides.push({ title: t('finale.letter'), lines: [finale.letter] });
    }

    return slides;
  }

  private promiseDialog(): HTMLElement {
    const { action } = this.promising!;
    const overlay = el('div', 'ws-overlay');
    const dialog = el('section', 'ws-dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'ws-promise-title');

    const title = el('h2', 'ws-dialog-title', t('promise.title'));
    title.id = 'ws-promise-title';
    dialog.append(title);

    const whatLabel = el('label', 'ws-field');
    whatLabel.append(el('span', 'ws-field-label', t('promise.what')));
    const what = el('input', 'ws-input');
    what.id = 'ws-promise-what';
    what.placeholder = t('promise.whatPlaceholder');
    what.value = this.promiseWhat;
    what.addEventListener('input', () => (this.promiseWhat = what.value));
    whatLabel.append(what);
    dialog.append(whatLabel);

    const byLabel = el('label', 'ws-field');
    byLabel.append(el('span', 'ws-field-label', t('promise.by')));
    const by = el('input', 'ws-input');
    by.id = 'ws-promise-by';
    by.type = 'number';
    by.value = this.promiseBy;
    by.addEventListener('input', () => (this.promiseBy = by.value));
    byLabel.append(by);
    dialog.append(byLabel);

    const row = el('div', 'ws-dialog-actions');
    row.append(
      button(t('promise.go'), 'ws-primary', () => {
        if (!this.promiseWhat.trim()) {
          // standards/messages.md: the refusal names the next move, and the reason it matters -
          // they will hold you to these words specifically.
          if (!dialog.querySelector('.ws-dialog-warn')) {
            dialog.append(el('p', 'ws-dialog-warn', t('promise.needWhat')));
          }
          return;
        }

        void this.api.dispatch({
          ...action.input,
          content: this.promiseWhat.trim(),
          dueRound: Number(this.promiseBy) || 0,
        });
        this.promising = null;
        this.render();
      }),
    );
    row.append(
      button(t('confirm.cancel'), 'ws-secondary', () => {
        this.promising = null;
        this.render();
      }),
    );
    dialog.append(row);
    overlay.append(dialog);
    return overlay;
  }

  private confirmDialog(model: WorkspaceModel): HTMLElement {
    const { action } = this.confirming!;
    const overlay = el('div', 'ws-overlay');
    const dialog = el('section', 'ws-dialog');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'ws-confirm-title');

    const title = el('h2', 'ws-dialog-title', t('confirm.title'));
    title.id = 'ws-confirm-title';
    dialog.append(title);
    dialog.append(el('p', 'ws-dialog-option', t('confirm.option', { label: action.label })));
    if (action.odds)
      dialog.append(el('p', 'ws-dialog-odds', t('incident.bet', { odds: action.odds })));

    if (action.stakes.length) {
      dialog.append(el('p', 'ws-dialog-lead', t('confirm.moves')));
      const list = el('ul', 'ws-dialog-stakes');
      for (const stake of action.stakes) list.append(el('li', undefined, stake));
      dialog.append(list);
    }

    let note: HTMLInputElement | null = null;
    if (action.ceremony === 'confirm-with-note') {
      const label = el('label', 'ws-field');
      label.append(el('span', 'ws-field-label', t('confirm.why')));
      note = el('input', 'ws-input');
      note.id = 'ws-confirm-note';
      note.placeholder = t('confirm.whyPlaceholder');
      // The server's own limit, so the field simply stops rather than the commit being refused.
      note.maxLength = MAX_REASON;
      note.value = this.confirmNote;
      note.addEventListener('input', () => (this.confirmNote = note!.value));
      label.append(note);
      dialog.append(label);
    }

    const row = el('div', 'ws-dialog-actions');
    row.append(
      button(t('confirm.go'), 'ws-primary', () => {
        if (action.ceremony === 'confirm-with-note' && this.confirmNote.trim().length === 0) {
          // The line is the point of this tier: it lands in the log and the retro quotes it back. A
          // confirm that accepts an empty one is a confirm that taught nothing.
          const warn = dialog.querySelector('.ws-dialog-warn');
          if (!warn) dialog.append(el('p', 'ws-dialog-warn', t('confirm.whyRequired')));
          return;
        }

        // The line goes to BOTH places it belongs, and they are different places for different readers.
        // The thread post is a message to colleagues - that is what it always was. The `reason` on the
        // input is the committed copy, and it is the one the retro quotes back (§2b): chat is not state,
        // and a justification that lived only in chat could never be read back to the player who wrote it.
        const why = this.confirmNote.trim();
        if (why) void this.api.say(why);
        void this.api.dispatch(withReason(action.input, why));
        this.confirming = null;
        this.confirmNote = '';
        this.render();
      }),
    );
    row.append(
      button(t('confirm.cancel'), 'ws-secondary', () => {
        this.confirming = null;
        this.render();
      }),
    );
    dialog.append(row);
    void model;
    overlay.append(dialog);
    return overlay;
  }

  private lobbyBar(model: WorkspaceModel): HTMLElement {
    const bar = el('section', 'ws-lobby');
    bar.append(el('h2', 'ws-lobby-title', t('lobby.title')));
    bar.append(el('p', 'ws-lobby-lead', t('lobby.subtitle')));

    for (const affordance of affordancesFor(model, { isOwner: this.api.isOwner() })) {
      if (affordance.surface !== 'room') continue;
      bar.append(
        button(affordance.label, 'ws-primary', () => void this.api.dispatch(affordance.action)),
      );
      if (affordance.hint) bar.append(el('p', 'ws-lobby-hint', affordance.hint));
    }

    return bar;
  }

  private composer(model: WorkspaceModel, channel: ChannelView): HTMLElement {
    const wrap = el('form', 'ws-composer');
    wrap.addEventListener('submit', (e) => e.preventDefault());

    if (!channel.placeholder) {
      return wrap;
    }

    // Your role's own verbs, where your hands already are (§2 "Seat verbs"). Every one of them is an
    // entry in the affordance catalog, and every one dispatches a canonical input.
    const verbs = affordancesFor(model, { isOwner: this.api.isOwner() }).filter(
      (a) => a.surface === 'composer',
    );
    if (verbs.length && channel.kind === 'channel') {
      const row = el('div', 'ws-verbs');
      row.append(el('span', 'ws-verbs-label', t('verb.title')));
      for (const verb of verbs) {
        row.append(button(verb.label, 'ws-verb', () => void this.api.dispatch(verb.action)));
      }
      wrap.append(row);
    }

    const row = el('div', 'ws-composer-row');
    const input = el('input', 'ws-input ws-composer-input');
    input.id = 'ws-composer';
    input.placeholder = channel.placeholder;
    input.setAttribute('aria-label', channel.placeholder);
    input.value = this.drafts.get(channel.id) ?? '';
    input.addEventListener('input', () => {
      this.drafts.set(channel.id, input.value);

      // The picker is a function of the caret, so it has to re-render as you type - that is the whole
      // feature: the list narrows while you are still typing the name.
      const next = mentionQuery(input.value, input.selectionStart ?? input.value.length);
      if (next !== this.mentioning) {
        this.mentioning = next;
        this.render();
      }
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.mentioning !== null) {
        // Dismiss without losing what you typed - the `@` stays, only the list goes.
        e.preventDefault();
        this.mentioning = null;
        this.render();
        return;
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send(channel);
      }
    });
    row.append(input);
    row.append(button(t('composer.send'), 'ws-secondary', () => this.send(channel)));
    wrap.append(row);

    // WHO YOU CAN MENTION (§3.5). A mention is addressed to a SEAT and the server parses the key, so a
    // player typing one from memory has to know a word this client has never shown them. The picker is
    // the fix, and it is the ordinary messenger gesture: type `@`, see who is there, keep typing to
    // narrow it.
    const picker = this.mentions(model, input, channel);
    if (picker) {
      wrap.append(picker);
    }

    // THE SIGN-OFF (§2m): the check-in, finally wearing its own ritual.
    const gesture = affordancesFor(model, { isOwner: this.api.isOwner() }).find(
      (a) => a.surface === 'gesture',
    );
    if (gesture) {
      wrap.append(this.signOff(model, gesture));
    } else if (model.you.signedOff) {
      wrap.append(el('p', 'ws-signed-off', t('gesture.signedOff')));
    }

    return wrap;
  }

  /**
   * The mention picker, or null when the composer is not in a mention.
   *
   * Real `<button>`s, which is this client's standing answer to §3: keyboard-operable for free, in the
   * tab order, and announced as buttons. No dropdown, no roving focus, no key handling of its own - the
   * simplest thing that is genuinely operable without a mouse.
   */
  private mentions(
    model: WorkspaceModel,
    input: HTMLInputElement,
    channel: ChannelView,
  ): HTMLElement | null {
    if (this.mentioning === null) {
      return null;
    }

    const candidates = mentionCandidates(model.members, this.mentioning);
    const wrap = el('div', 'ws-mentions');
    wrap.setAttribute('aria-label', t('mention.label'));

    if (candidates.length === 0) {
      // Say so rather than vanishing: a picker that disappears reads as the feature being broken, and
      // the player is left wondering whether they typed the name wrong or mentions do not work here.
      wrap.append(el('p', 'ws-mentions-empty', t('mention.none', { prefix: this.mentioning })));
      return wrap;
    }

    for (const who of candidates) {
      const label = who.role ? `${who.who.name} · ${who.role}` : who.who.name;
      wrap.append(
        button(label, 'ws-mention', () => {
          const applied = applyMention(
            input.value,
            input.selectionStart ?? input.value.length,
            mentionSeat(who),
          );
          this.drafts.set(channel.id, applied.text);
          this.mentioning = null;
          this.render();

          // Back to the composer with the caret after the name: the point of picking is to carry on
          // writing the sentence, not to go looking for the box again.
          const again = this.mount.querySelector('#ws-composer');
          if (again instanceof HTMLInputElement) {
            again.focus();
            again.setSelectionRange(applied.caret, applied.caret);
          }
        }),
      );
    }

    return wrap;
  }

  private signOff(model: WorkspaceModel, gesture: Affordance): HTMLElement {
    const wrap = el('div', 'ws-signoff');
    if (!this.signingOff) {
      wrap.append(
        button(gesture.label, 'ws-quiet', () => {
          this.signingOff = true;
          this.render();
        }),
      );
      if (gesture.hint) wrap.append(el('span', 'ws-signoff-hint', gesture.hint));
      return wrap;
    }

    // The mood two-tap rides the same gesture (§2m): one ritual, two signals for the director.
    wrap.append(el('span', 'ws-signoff-ask', t('gesture.moodAsk')));
    const seat = model.you.seat!;
    for (const { key, mood } of MOODS) {
      wrap.append(
        button(t(key), 'ws-mood', () => {
          this.signingOff = false;
          void this.api.signOff(seat, mood);
          this.render();
        }),
      );
    }
    wrap.append(
      button(t('gesture.moodSkip'), 'ws-quiet', () => {
        this.signingOff = false;
        void this.api.signOff(seat, null);
        this.render();
      }),
    );
    return wrap;
  }

  private send(channel: ChannelView): void {
    const text = (this.drafts.get(channel.id) ?? '').trim();
    if (!text) return;
    this.drafts.set(channel.id, '');

    if (channel.kind === 'dm') {
      // The buddy is answered client-side and scripted (§1a); a colleague's DM is free text that goes
      // to the server and comes back compiled to a topic the engine can ground (§2c). Two DMs, two
      // very different contracts - and the id says which, because it is the seat.
      if (channel.id === DM_BUDDY) {
        this.api.sayToBuddy(text);
      } else {
        void this.api.askColleague(channel.id.replace('dm:seat:', ''), text);
      }
      this.render();
      return;
    }

    // The ROOM it was typed in (register item 16). This is the whole bug: the composer used to send
    // every line into the one chat that existed, so `#war-room` prose surfaced in `#team`.
    void this.api.say(text, channel.id);

    // A prose command posts as chat, because it IS chat — and then the buddy says where that move
    // actually lives, so it never lands in silence (§1a). The answer arrives in the DM, which is where
    // onboarding lives; it does not interrupt the room.
    if (readsAsCommand(text)) {
      this.api.sayToBuddy(text);
    }

    this.render();
  }

  private people(model: WorkspaceModel): HTMLElement {
    const aside = el('aside', 'ws-people');
    aside.setAttribute('aria-label', t('app.people'));
    aside.append(el('h2', 'ws-people-heading', t('app.people')));

    const affordances = affordancesFor(model, { isOwner: this.api.isOwner() });
    const list = el('ul', 'ws-people-list');

    for (const member of model.members) {
      const row = el('li', 'ws-person');
      row.append(avatar(member.who, 'sm'));

      const body = el('div', 'ws-person-body');
      body.append(
        el(
          'span',
          'ws-person-name',
          member.mine ? t('member.you', { name: member.who.name }) : member.who.name,
        ),
      );
      if (member.who.subtitle) body.append(el('span', 'ws-person-role', member.who.subtitle));

      // Presence is LIVE HUMANS ONLY (§1b). An NPC has no dot; a human who drifted leaves no headstone,
      // because there is nothing here that renders "last seen".
      if (member.presence) {
        const dot = el('span', `ws-dot is-${member.presence}`);
        dot.setAttribute('role', 'img');
        dot.setAttribute('aria-label', t(`presence.${member.presence}` as 'presence.here'));
        body.append(dot);
      }

      const take = affordances.find((a) => a.id === `claim:${member.role}`);
      if (take) {
        body.append(el('span', 'ws-person-open', t('lobby.roleOpen')));
        body.append(button(take.label, 'ws-secondary', () => void this.api.dispatch(take.action)));
        if (take.hint) body.append(el('span', 'ws-person-does', take.hint));
      } else if (member.does.length && member.mine) {
        body.append(
          el(
            'span',
            'ws-person-does',
            t('lobby.roleDoes', { powers: member.does.map(verbLabel).join(', ') }),
          ),
        );
      }

      row.append(body);
      list.append(row);
    }

    aside.append(list);
    return aside;
  }
}
